// feat_380 — criterio ÚNICO de pertenencia de un ticket a una lista de tickets.
//
// 🔴 EL PROBLEMA que resuelve: desde el refactor del socket singleton
// (2026-09-18, commits 80c62b0d/3b3d23bd) todas las listas comparten un socket
// unido a todas las salas (pending-<cid>, open-<cid>, notification-<cid>), así
// que CADA lista recibe CADA appMessage. handleTicket ya filtraba por
// pertenencia (inline), pero handleAppMessage sólo miraba shouldUpdateTicket
// ("¿este usuario puede ver este ticket?") y no "¿este ticket pertenece a esta
// lista?": un ticket open+cola entraba a EN ESPERA en modo smart/split → la
// misma fila en EN ATENCIÓN y EN ESPERA hasta recargar.
//
// ⛔ LA INVARIANTE (cubierta por belongsToThisList.test.mjs): un evento de
// socket nunca agrega un ticket a una lista cuyo filtro no cumple. Los dos
// handlers (handleTicket y handleAppMessage) usan ESTA función; si cambia un
// filtro del fetch HTTP, cambia acá y en la tabla de casos del test.
//
// Vive en su propio archivo (y no dentro del componente) para que el test pueda
// evaluarlo sin arrastrar React ni MUI.

// senderTypes que el backend vuelca en Ticket.lastMessageSenderType. Los demás
// (internal-note, scheduled-event, turnos-advisor) NO lo tocan: si un appMessage
// de una nota interna pisara el eje "sin responder", el ticket saldría de la
// lista de no respondidos aunque el cliente siga esperando respuesta.
export const SENDER_TYPES_THAT_ANSWER = [
	"customer",
	"advisor",
	"ai",
	"system",
	"matchprop-alert",
];

export const makeBelongsToThisList = ({
	status,
	queueAssignment,
	onlyUnanswered,
	filterUserIds,
	includeUnassigned,
}) => {
	// senderTypeOverride: cuando el chequeo se hace a raíz de un appMessage
	// nuevo, data.message.senderType es FRESCO (ticket.lastMessageSenderType
	// del payload puede venir desfasado por la carrera de
	// SendWhatsAppMessage.ts:97, que actualiza el ticket DESPUÉS de que
	// CreateMessageService cargó el ticket que viaja en el emit). Sólo cuenta si
	// es un senderType que el backend vuelca al ticket; si no, cae al ticket.
	return (ticket, senderTypeOverride) => {
		// feat_323 — con `status` array (bandeja unificada) un `!==` sería
		// SIEMPRE true y borraría toda fila que llegue por socket: la lista
		// se vaciaría sola. El criterio pasa a ser pertenencia al conjunto.
		const statusMismatch = Array.isArray(status)
			? !status.includes(ticket.status)
			: !!status && ticket.status !== status;

		// feat_325 — el status NO alcanza en modo smart. Las dos listas se
		// unen a la MISMA sala `pending-<cid>` (el socket del navegador es
		// singleton multiplexado), así que cuando a un huérfano le asignan
		// cola el evento llega a las dos con el status SIN cambiar: la de
		// huérfanos no lo sacaría y la de asignados lo agregaría → la misma
		// fila en las dos listas hasta refrescar. El bucket se decide por la
		// presencia de cola, igual que el WHERE del fetch.
		const queueMismatch =
			(queueAssignment === "unassigned" && ticket.queueId != null) ||
			(queueAssignment === "assigned" && ticket.queueId == null) ||
			(queueAssignment === "assigned_or_open" &&
				ticket.queueId == null &&
				ticket.status !== "open");

		// feat_326 — mismo razonamiento que queueMismatch, pero por el
		// criterio del filtro "sin responder": si el asesor contesta, el
		// ticket deja de pertenecer a esta lista aunque su status y su
		// cola no hayan cambiado. `lastMessageSenderType` viaja en el
		// payload del socket (ShowTicketService lo devuelve).
		const effectiveSenderType = SENDER_TYPES_THAT_ANSWER.includes(
			senderTypeOverride
		)
			? senderTypeOverride
			: ticket.lastMessageSenderType;
		const answeredMismatch =
			!!onlyUnanswered && effectiveSenderType !== "customer";

		// feat_327 — el guard de ALTA (`shouldUpdateTicket`, feat_301) ya
		// filtraba por usuario, pero el de BAJA no: devolver `false` ahí
		// significa "no hacer nada", NO "sacarlo". Un ticket reasignado a
		// otro asesor (o desasignado) con el status, la cola y el
		// `lastMessageSenderType` SIN cambiar quedaba colgado en la lista
		// filtrada hasta recargar. Misma clase que queueMismatch (feat_325)
		// y answeredMismatch (feat_326).
		// Replica el OR del backend (ListTicketsService feat_301):
		//   userId IN ids  OR  (includeUnassigned AND userId IS NULL)
		// ⚠ Es un OR, no un AND: con `includeUnassigned` prendido los
		// tickets sin dueño se QUEDAN aunque haya `filterUserIds`.
		// Gateado por `userFilterActive` → filtro apagado = false = cero
		// regresión. `userId` viaja siempre en el payload (ShowTicketService
		// no restringe attributes del Ticket) y es `null` para sin asignar.
		const userFilterActive =
			filterUserIds?.length > 0 || !!includeUnassigned;
		const userMismatch =
			userFilterActive &&
			!(filterUserIds || []).includes(Number(ticket.userId)) &&
			!(includeUnassigned && !ticket.userId);

		return !(statusMismatch || queueMismatch || answeredMismatch || userMismatch);
	};
};
